<template>
  <benchmark-group-table
    v-if="loading || props.metrics.length"
    title="Workforce Impact: Enrolled vs Non-Enrolled"
    :has-subtitle="!!props.metrics.length"
    :columns="COLUMNS"
    :groups="computedGroups"
    :loading="loading"
  >
    <template #subtitle> Comparing outcomes for enrolled employees versus non-enrolled employees. </template>
  </benchmark-group-table>
</template>

<script setup>
import { computed } from 'vue';
import { WORKFORCE_IMPACT_GROUPS } from '@/pages/analyticsBenchmark/benchmarkConfig';
import { buildMetricsMap, calculateDelta, formatValue } from '@/pages/analyticsBenchmark/benchmarkUtils';
import BenchmarkGroupTable from '@/pages/analyticsBenchmark/components/BenchmarkGroupTable.vue';

const COLUMNS = [
  { key: 'name', label: 'Metric' },
  { key: 'enrolled', label: 'Enrolled' },
  { key: 'nonEnrolled', label: 'Non-Enrolled' },
  { key: 'gap', label: 'Delta' },
];

const props = defineProps({
  metrics: { type: Array, default: () => [] },
  sector: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

const metricsMap = computed(() => buildMetricsMap(props.metrics));

const computedGroups = computed(() =>
  WORKFORCE_IMPACT_GROUPS.map(group => ({
    name: group.name,
    emoji: group.emoji,
    color: group.color,
    rows: group.metrics.map(config => {
      const enrolledMetric = metricsMap.value[config.enrolledMetricId];
      const enrolledValue = enrolledMetric?.company_value ?? null;
      const unit = enrolledMetric?.unit || 'percent';

      let nonEnrolledValue = null;
      let col2Label = null;
      let col2Display;

      if (config.nonEnrolledDisplay === 'dash') {
        col2Display = '—';
      } else if (config.nonEnrolledDisplay === 'sector') {
        // Special case: show sector_value from the enrolled metric with "(sector)" label
        nonEnrolledValue = enrolledMetric?.sector_value ?? null;
        col2Display = formatValue(nonEnrolledValue, unit);
        col2Label = 'sector';
      } else {
        const nonEnrolledMetric = config.nonEnrolledMetricId ? metricsMap.value[config.nonEnrolledMetricId] : null;
        nonEnrolledValue = nonEnrolledMetric?.company_value ?? null;
        col2Display = formatValue(nonEnrolledValue, unit);
      }

      const showGap = config.nonEnrolledDisplay !== 'dash';
      const delta = showGap
        ? calculateDelta(enrolledValue, nonEnrolledValue, {
            deltaFormat: config.deltaFormat,
            polarity: config.polarity,
            unit,
          })
        : { raw: null, formatted: '—', colorClass: 'delta-neutral' };

      return {
        id: config.enrolledMetricId,
        label: config.label,
        description: enrolledMetric?.description || null,
        tooltipId: `wi-${config.enrolledMetricId}`,
        col1: formatValue(enrolledValue, unit),
        col2: col2Display,
        col2Label,
        deltaFormatted: delta.formatted,
        deltaColorClass: delta.colorClass,
      };
    }),
  })),
);
</script>
